/**
 * SavedViewMenu — the mobile (< md) presentation of the saved-view selector.
 *
 * The pill strip wraps onto several lines on a phone and its manage caret is
 * hover-only, which touch can't reach. Here the whole selector is one
 * pill-shaped trigger showing the active view, opening a menu that lists
 * every view, then the actions for the active view: Save / Discard while it
 * has unsaved changes, and rename / share / default / delete for the active
 * saved view. An icon-only "New view" button sits beside the trigger.
 */

import { Check, ChevronDown, List, Plus } from '@untitledui/icons';
import type React from 'react';
import { memo, useCallback, useMemo } from 'react';
import { Button as AriaButton } from 'react-aria-components';

import { Dropdown } from '@/lib/base/overlays/dropdown';
import type { BuiltInViewConfig } from '@/lib/crm/core/types';
import type { SavedFilterView } from '@/types/savedFilterView';
import { cx } from '@/utils/cx';

import { isModifiedClick } from './buildViewHref';
import {
  CARET_ICON_CLASS,
  DefaultOnlyMenuItem,
  DirtyDot,
  DirtyViewMenuItems,
  ManageSavedViewMenuItems,
  PILL_ACTIVE,
  PILL_BASE,
  PILL_INACTIVE,
  VIEW_ICON_CLASS,
} from './SavedViewMenuItems';
import {
  isDefaultRef,
  type SavedViewPresentationProps,
} from './savedViewTypes';

const ALL_KEY = 'all';
const BUILT_IN_KEY_PREFIX = 'builtin:';
const SAVED_KEY_PREFIX = 'saved:';

const TRIGGER_CLASS = cx(PILL_BASE, PILL_ACTIVE, 'min-w-0');

// Icon-only "New view" beside the trigger (desktop shows the label). A 28px
// square so it lines up with the trigger pill's height.
const NEW_VIEW_CLASS = cx(
  PILL_BASE,
  PILL_INACTIVE,
  'size-7 shrink-0 justify-center px-0'
);

const DEFAULT_ADDON = 'Default';

// ---------------------------------------------------------------------------
// Per-row menu items — memoized so a menu re-render doesn't rebuild every
// row's onClick handler (see SavedViewPills.tsx for the same pattern on the
// desktop strip).
// ---------------------------------------------------------------------------

interface BuiltInMenuItemProps {
  view: BuiltInViewConfig;
  href: string;
  isActive: boolean;
  isDefault: boolean;
  onSelect: (viewId: string) => void;
}

const BuiltInMenuItem = memo(function BuiltInMenuItem({
  view,
  href,
  isActive,
  isDefault,
  onSelect,
}: BuiltInMenuItemProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      onSelect(view.id);
    },
    [onSelect, view.id]
  );

  return (
    <Dropdown.Item
      id={`${BUILT_IN_KEY_PREFIX}${view.id}`}
      href={href}
      onClick={handleClick}
      icon={isActive ? Check : List}
      label={view.label}
      textValue={view.label}
      addon={isDefault ? DEFAULT_ADDON : undefined}
    />
  );
});

interface AllMenuItemProps {
  href: string;
  isActive: boolean;
  isDefault: boolean;
  onSelect: () => void;
}

const AllMenuItem = memo(function AllMenuItem({
  href,
  isActive,
  isDefault,
  onSelect,
}: AllMenuItemProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      onSelect();
    },
    [onSelect]
  );

  return (
    <Dropdown.Item
      id={ALL_KEY}
      href={href}
      onClick={handleClick}
      icon={isActive ? Check : List}
      label='All'
      textValue='All'
      addon={isDefault ? DEFAULT_ADDON : undefined}
    />
  );
});

interface SavedMenuItemProps {
  view: SavedFilterView;
  href: string;
  isActive: boolean;
  isDefault: boolean;
  onSelect: (viewId: string) => void;
}

const SavedMenuItem = memo(function SavedMenuItem({
  view,
  href,
  isActive,
  isDefault,
  onSelect,
}: SavedMenuItemProps) {
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (isModifiedClick(e)) {
        return;
      }
      e.preventDefault();
      onSelect(view.entity_id);
    },
    [onSelect, view.entity_id]
  );

  return (
    <Dropdown.Item
      id={`${SAVED_KEY_PREFIX}${view.entity_id}`}
      href={href}
      onClick={handleClick}
      icon={isActive ? Check : List}
      label={view.name}
      textValue={view.name}
      addon={isDefault ? DEFAULT_ADDON : undefined}
    />
  );
});

export const SavedViewMenu = memo(function SavedViewMenu({
  activeViewId,
  orderedViews,
  builtInViews,
  activeBuiltInViewId,
  onSelectBuiltInView,
  onSelectView,
  onSelectAll,
  onSaveCurrentView,
  defaultViewRef,
  hasDefaultViewFeature,
  onToggleDefaultAll,
  onToggleDefaultBuiltIn,
  onToggleDefaultSaved,
  onRename,
  onToggleShare,
  onDelete,
  isMutationPending,
  viewDirty,
  dirtyActions,
  canCreateView,
  hrefForView,
}: SavedViewPresentationProps) {
  const activeSavedView = useMemo(
    () =>
      activeViewId === null
        ? null
        : (orderedViews.find(v => v.entity_id === activeViewId) ?? null),
    [activeViewId, orderedViews]
  );
  const activeBuiltInView = useMemo(
    () =>
      activeViewId === null && builtInViews
        ? (builtInViews.find(v => v.id === activeBuiltInViewId) ?? null)
        : null,
    [activeViewId, builtInViews, activeBuiltInViewId]
  );

  const triggerLabel =
    activeSavedView?.name ?? activeBuiltInView?.label ?? 'All';

  // The active view's row swaps its list icon for a check. Selection mode is
  // deliberately 'none': with react-aria's single-selection every row —
  // including the save / manage actions below — would be announced as a
  // radio, which they are not.
  let activeKey: string;
  if (activeSavedView) {
    activeKey = `${SAVED_KEY_PREFIX}${activeSavedView.entity_id}`;
  } else if (activeBuiltInView) {
    activeKey = `${BUILT_IN_KEY_PREFIX}${activeBuiltInView.id}`;
  } else {
    activeKey = ALL_KEY;
  }

  // View selection is routed per-item via href + onClick (modifier-aware link
  // items), so a plain click applies the view client-side while Cmd/Ctrl/
  // Shift/middle-click falls through to the browser's own navigation.

  const handleRenameActive = useCallback(() => {
    if (activeSavedView) {
      onRename(activeSavedView);
    }
  }, [activeSavedView, onRename]);
  const handleToggleShareActive = useCallback(() => {
    if (activeSavedView) {
      onToggleShare(activeSavedView);
    }
  }, [activeSavedView, onToggleShare]);
  const handleDeleteActive = useCallback(() => {
    if (activeSavedView) {
      onDelete(activeSavedView);
    }
  }, [activeSavedView, onDelete]);
  const handleToggleDefaultActive = useCallback(() => {
    if (activeSavedView) {
      onToggleDefaultSaved(activeSavedView);
    } else if (activeBuiltInView) {
      onToggleDefaultBuiltIn(activeBuiltInView.id);
    } else {
      onToggleDefaultAll();
    }
  }, [
    activeSavedView,
    activeBuiltInView,
    onToggleDefaultSaved,
    onToggleDefaultBuiltIn,
    onToggleDefaultAll,
  ]);

  const isAllDefault = isDefaultRef(defaultViewRef, { kind: 'all' });
  let activeIsDefault: boolean;
  if (activeSavedView) {
    activeIsDefault = isDefaultRef(defaultViewRef, {
      kind: 'saved',
      id: activeSavedView.entity_id,
    });
  } else if (activeBuiltInView) {
    activeIsDefault = isDefaultRef(defaultViewRef, {
      kind: 'builtin',
      id: activeBuiltInView.id,
    });
  } else {
    activeIsDefault = isAllDefault;
  }

  const isDirty = viewDirty && activeSavedView !== null;
  // No keyboard-chord hint on a phone.
  const mobileDirtyActions = useMemo(
    () => ({ ...dirtyActions, shortcutHint: undefined }),
    [dirtyActions]
  );
  const showManageActive =
    activeSavedView !== null &&
    (activeSavedView.is_owner || hasDefaultViewFeature);
  const showDefaultOnlyActive =
    activeSavedView === null && hasDefaultViewFeature;

  return (
    <div className='flex min-w-0 items-center gap-0.5'>
      <Dropdown.Root instance='saved-views'>
        <AriaButton className={TRIGGER_CLASS}>
          <List className={VIEW_ICON_CLASS} />
          <span className='truncate'>{triggerLabel}</span>
          {isDirty && <DirtyDot />}
          <ChevronDown className={cx(CARET_ICON_CLASS, 'text-fg-quaternary')} />
        </AriaButton>
        <Dropdown.Popover placement='bottom left'>
          <Dropdown.Menu aria-label='Views' selectionMode='none'>
            {builtInViews ? (
              builtInViews.map(v => (
                <BuiltInMenuItem
                  key={v.id}
                  view={v}
                  href={hrefForView({ kind: 'builtin', id: v.id })}
                  isActive={`${BUILT_IN_KEY_PREFIX}${v.id}` === activeKey}
                  isDefault={isDefaultRef(defaultViewRef, {
                    kind: 'builtin',
                    id: v.id,
                  })}
                  onSelect={onSelectBuiltInView ?? onSelectAll}
                />
              ))
            ) : (
              <AllMenuItem
                href={hrefForView(null)}
                isActive={activeKey === ALL_KEY}
                isDefault={isAllDefault}
                onSelect={onSelectAll}
              />
            )}

            {orderedViews.length > 0 && <Dropdown.Separator />}

            {orderedViews.map(v => (
              <SavedMenuItem
                key={v.entity_id}
                view={v}
                href={hrefForView({ kind: 'saved', id: v.entity_id })}
                isActive={`${SAVED_KEY_PREFIX}${v.entity_id}` === activeKey}
                isDefault={isDefaultRef(defaultViewRef, {
                  kind: 'saved',
                  id: v.entity_id,
                })}
                onSelect={onSelectView}
              />
            ))}

            {isDirty && <Dropdown.Separator />}
            {isDirty && <DirtyViewMenuItems actions={mobileDirtyActions} />}

            {(showManageActive || showDefaultOnlyActive) && (
              <Dropdown.Separator />
            )}

            {showManageActive && activeSavedView && (
              <ManageSavedViewMenuItems
                view={activeSavedView}
                isDefault={activeIsDefault}
                showDefaultItem={hasDefaultViewFeature}
                isMutationPending={isMutationPending}
                onRename={handleRenameActive}
                onToggleShare={handleToggleShareActive}
                onToggleDefault={handleToggleDefaultActive}
                onDelete={handleDeleteActive}
              />
            )}

            {showDefaultOnlyActive && (
              <DefaultOnlyMenuItem
                isDefault={activeIsDefault}
                onToggleDefault={handleToggleDefaultActive}
              />
            )}
          </Dropdown.Menu>
        </Dropdown.Popover>
      </Dropdown.Root>

      {/* Icon-only on a phone, like the header's other actions; the
          accessible name carries the label. Opens "Save current view". */}
      {canCreateView && (
        <AriaButton
          aria-label='New view'
          onPress={onSaveCurrentView}
          className={NEW_VIEW_CLASS}
        >
          <Plus className={VIEW_ICON_CLASS} />
        </AriaButton>
      )}
    </div>
  );
});
